import React, { useCallback, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ApprovalRequestStatus } from '../../../../__generated__/graphql';
import { useToasts } from '../../../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../../../hooks/useMutationErrorHandler';
import { useUpdateApprovalRequestStatus } from './useObjectiveMutations';
import Button from '../../../../../../components/button';

type Props = {
  approvalRequestId: string;
  onComplete: () => void;
};

const i18p = 'performanceReview.goals2.approvalStatus.';

export const GoalApprovalActions: React.FC<Props> = ({
  approvalRequestId,
  onComplete,
}) => {
  const { t } = useTranslation();
  const { addSuccess } = useToasts();
  const errorHandler = useMutationErrorHandler();
  const [showRejectComment, setShowRejectComment] = useState(false);
  const [comment, setComment] = useState('');
  const [loadingAction, setLoadingAction] = useState<
    'approve' | 'reject' | null
  >(null);

  const [updateStatus] = useUpdateApprovalRequestStatus();

  const handleApprove = useCallback(async () => {
    setLoadingAction('approve');
    try {
      await updateStatus({
        variables: {
          id: approvalRequestId,
          status: ApprovalRequestStatus.Approved,
          comment: null,
        },
      });
      addSuccess(t(i18p + 'approved'));
      onComplete();
    } catch (e) {
      errorHandler(e);
    } finally {
      setLoadingAction(null);
    }
  }, [
    approvalRequestId,
    updateStatus,
    addSuccess,
    t,
    onComplete,
    errorHandler,
  ]);

  const handleReject = useCallback(async () => {
    if (!showRejectComment) {
      setShowRejectComment(true);
      return;
    }
    setLoadingAction('reject');
    try {
      await updateStatus({
        variables: {
          id: approvalRequestId,
          status: ApprovalRequestStatus.Rejected,
          comment: comment || null,
        },
      });
      addSuccess(t(i18p + 'rejectedSuccess'));
      onComplete();
    } catch (e) {
      errorHandler(e);
    } finally {
      setLoadingAction(null);
    }
  }, [
    approvalRequestId,
    showRejectComment,
    comment,
    updateStatus,
    addSuccess,
    t,
    onComplete,
    errorHandler,
  ]);

  return (
    <div className="d-flex flex-wrap gap-2 align-items-start">
      <Button
        variant="success"
        size="sm"
        isProcessing={loadingAction === 'approve'}
        disabled={loadingAction !== null}
        label={t(i18p + 'approve')}
        onClick={handleApprove}
      />
      <Button
        variant="danger"
        size="sm"
        isProcessing={loadingAction === 'reject'}
        disabled={loadingAction !== null}
        label={t(i18p + 'reject')}
        onClick={handleReject}
      />
      {showRejectComment && (
        <>
          <Form.Control
            as="textarea"
            rows={2}
            size="sm"
            placeholder={t(i18p + 'rejectCommentPlaceholder')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={loadingAction !== null}
            label={t(i18p + 'cancelReject')}
            onClick={() => {
              setShowRejectComment(false);
              setComment('');
            }}
          />
        </>
      )}
    </div>
  );
};
